import { useEffect, useRef } from 'react'

export interface ShortcutHandlers {
  openPalette: () => void
  showHelp: () => void
  lock: () => void
  goto: (path: string) => void
}

/** Where `g` takes you. The letters follow the German labels, which is what is on screen. */
export const GOTO_TARGETS: { key: string; path: string; label: string }[] = [
  { key: 'u', path: '/', label: 'Übersicht' },
  { key: 's', path: '/server', label: 'Server' },
  { key: 'p', path: '/projekte', label: 'Projekte' },
  { key: 'b', path: '/backups', label: 'Backups' },
  { key: 'a', path: '/alarme', label: 'Alarme' },
  { key: 'n', path: '/snippets', label: 'Snippets' },
  { key: 'r', path: '/berichte', label: 'Berichte' },
  { key: 'e', path: '/einstellungen', label: 'Einstellungen' },
]

/** How long the `g` prefix stays armed. Long enough to think, short enough not to surprise. */
const SEQUENCE_TIMEOUT_MS = 1200

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * True inside the terminal. xterm.js puts a hidden textarea under `.xterm` and forwards every
 * keystroke to the remote shell — Ctrl+K is "kill to end of line" there, Ctrl+L clears the
 * screen. Stealing those would break the very thing this app exists for, so inside the terminal
 * only the modifier combination no shell claims (Ctrl+Shift+P) is taken.
 */
function isTerminal(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.closest('.xterm') !== null
}

/** Global keyboard shortcuts (PROMPT.md §7 phase 8). */
export function useShortcuts(handlers: ShortcutHandlers): void {
  // Kept in a ref so the listener is attached once and still sees current callbacks; synced from
  // an effect, because writing a ref during render is not allowed.
  const latest = useRef(handlers)
  useEffect(() => {
    latest.current = handlers
  })

  useEffect(() => {
    let pendingGoto = 0

    function onKeyDown(event: KeyboardEvent): void {
      const terminal = isTerminal(event.target)

      // The one combination that also works while the terminal has focus.
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault()
        latest.current.openPalette()
        return
      }

      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'l') {
        event.preventDefault()
        latest.current.lock()
        return
      }

      if (terminal) return

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        latest.current.openPalette()
        return
      }

      // Everything below is a bare letter, so it must never fire while someone is writing.
      if (event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) return

      if (event.key === '?') {
        event.preventDefault()
        latest.current.showHelp()
        return
      }

      if (event.key === 'g') {
        pendingGoto = Date.now()
        return
      }

      if (pendingGoto !== 0 && Date.now() - pendingGoto < SEQUENCE_TIMEOUT_MS) {
        const target = GOTO_TARGETS.find((entry) => entry.key === event.key.toLowerCase())
        pendingGoto = 0
        if (target) {
          event.preventDefault()
          latest.current.goto(target.path)
        }
        return
      }

      pendingGoto = 0
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
